// src/pages/Residential.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "../components/Projects.css";

const pub = process.env.PUBLIC_URL;

const categories = ["All", "Residential", "Commercial", "Industrial", "Infrastructure"];

const allProjects = [
  {
    id: 1,
    title: "LPG Skid Meter - Francistown",
    description: "Explosion-proof mass-flow metering skid",
    image: `${pub}/LPG consumption customers3.jpg`,
    categories: ["industrial", "infrastructure"],
  },
  {
    id: 2,
    title: "Outdoor Stainless DB",
    description: "IP66 distribution board (hazardous area)",
    image: `${pub}/LPG consumption customers5.jpg`,
    categories: ["industrial"],
  },
  {
    id: 3,
    title: "6 kW Residential Solar",
    description: "Tile-roof mono-PERC array - Pretoria East",
    image: `${pub}/LPG consumption customers8.jpg`,
    categories: ["residential"],
  },
  {
    id: 4,
    title: "5 kVA Hybrid Inverter & LiFePO Batteries",
    description: "Wall-mount AC-DC power centre (10 kWh)",
    image: `${pub}/LPG consumption customers9.jpg`,
    categories: ["residential", "infrastructure"],
  },
  {
    id: 5,
    title: "Tzaneen LPG Depot MCC",
    description: "New MCC cabinet with underground feeds",
    image: `${pub}/Tzaneen LPG depot installation3.jpg`,
    categories: ["industrial", "infrastructure"],
  },
  {
    id: 6,
    title: "LPG Pump MCC - Operator Panel",
    description: "Front-facing indicators & selector switches",
    image: `${pub}/Panel wiring1.jpg`,
    categories: ["industrial", "commercial"],
  },
];

export default function Residential() {
  const [activeCat, setActiveCat] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [openProject, setOpenProject] = useState(null);
  const lastFocusRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 700, once: false, delay: 50 });
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    const body = document.body;
    if (openProject) {
      const prev = body.style.overflow;
      body.style.overflow = "hidden";
      return () => {
        body.style.overflow = prev;
      };
    }
  }, [openProject]);

  const filtered = allProjects.filter((project) => {
    const matchesCat =
      activeCat === "All" || project.categories.includes(activeCat.toLowerCase());
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const handleOpenModal = (project, e) => {
    if (e) {
      e.preventDefault();
      lastFocusRef.current = e.currentTarget;
    }
    setOpenProject(project);
    setTimeout(() => {
      document.getElementById("project-dialog")?.focus();
    }, 0);
  };

  const handleCloseModal = () => {
    setOpenProject(null);
    if (lastFocusRef.current && lastFocusRef.current.focus) {
      lastFocusRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") handleCloseModal();
  };

  return (
    <>
      {/* Scoped modal styles */}
      <style>{`
        .project-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: grid;
          place-items: center;
          z-index: 1000;
        }
        .project-modal {
          width: min(720px, 92vw);
          background: #ffffff;
          border-radius: 14px;
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.25);
          overflow: hidden;
          outline: none;
        }
        .project-modal__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.25rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .project-modal__title {
          margin: 0;
          font-size: 1.15rem;
          font-weight: 700;
        }
        .project-modal__close {
          border: none;
          background: transparent;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
        }
        .project-modal__body {
          display: grid;
          gap: 1rem;
          padding: 1rem 1.25rem 1.25rem;
        }
        @media (min-width: 720px) {
          .project-modal__body {
            grid-template-columns: 1.1fr 1.4fr;
            align-items: start;
          }
        }
        .project-modal__image {
          width: 100%;
          max-height: 320px;
          object-fit: cover;
          border-radius: 10px;
        }
        .project-modal__desc {
          font-size: 0.98rem;
          line-height: 1.55;
          margin-bottom: 0.75rem;
        }
        .project-modal__meta {
          font-size: 0.9rem;
          color: #6b7280;
          margin-bottom: 0.75rem;
        }
        .project-modal__tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          margin-bottom: 0.75rem;
        }
        .project-modal__tag {
          font-size: 0.78rem;
          padding: 0.2rem 0.6rem;
          border-radius: 999px;
          background: rgba(219, 52, 52, 0.08);
          color: #b91c1c;
        }
        .project-modal__actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }
        .project-modal__btn-primary {
          border: none;
          background: var(--primary-color, #db3434);
          color: #fff;
          padding: 0.5rem 1rem;
          border-radius: 999px;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
        }
        .project-modal__btn-outline {
          border: 1px solid var(--primary-color, #db3434);
          background: #fff;
          color: var(--primary-color, #db3434);
          padding: 0.5rem 1rem;
          border-radius: 999px;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
        }
      `}</style>

      <section className="page-breadcrumb-section" data-aos="fade-up">
        <h1 className="section-title">Projects</h1>
        <p className="page-breadcrumb">
          <Link to="/">Home</Link> / <span>Projects</span>
        </p>
      </section>

      <section className="projects-section" data-aos="fade-up">
        <div className="project-filter">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`filter-btn${activeCat === cat ? " active" : ""}`}
              onClick={() => setActiveCat(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="project-search">
          <input
            type="search"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <button type="button">
            <i className="fas fa-search" />
          </button>
        </div>

        <div className="projects-grid">
          {filtered.map((project) => (
            <div
              className="project-card"
              key={project.id}
              data-aos="zoom-in"
            >
              <div className="project-image">
                <img src={project.image} alt={project.title} />
              </div>
              <div className="project-info">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <Link
                  to="#"
                  className="btn-details"
                  onClick={(e) => handleOpenModal(project, e)}
                >
                  View Details
                </Link>
              </div>
            </div>
          ))}

          {filtered.length === 0 && (
            <p className="no-projects">No projects match your search.</p>
          )}
        </div>
      </section>

      <section className="cta-section" data-aos="fade-up">
        <div className="cta-content">
          <h2>Ready to Power Your Project?</h2>
          <p>Contact us today for a free consultation and quote.</p>
          <div className="cta-buttons">
            <a href="tel:+27824989477" className="btn btn-white">
              <i className="fas fa-phone" /> Call (+27)&nbsp;82&nbsp;498&nbsp;9477
            </a>
            <Link to="/contact" className="btn btn-outline">
              <i className="fas fa-envelope" /> Email Us
            </Link>
          </div>
        </div>
      </section>

      {/* Modal */}
      {openProject && (
        <div
          className="project-modal-overlay"
          onClick={handleCloseModal}
          onKeyDown={handleKeyDown}
          aria-hidden="false"
        >
          <div
            id="project-dialog"
            className="project-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="project-dialog-title"
            aria-describedby="project-dialog-desc"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="project-modal__header">
              <h3
                id="project-dialog-title"
                className="project-modal__title"
              >
                {openProject.title}
              </h3>
              <button
                type="button"
                className="project-modal__close"
                aria-label="Close"
                onClick={handleCloseModal}
              >
                &times;
              </button>
            </div>

            <div className="project-modal__body">
              <img
                src={openProject.image}
                alt={openProject.title}
                className="project-modal__image"
              />
              <div>
                <p
                  id="project-dialog-desc"
                  className="project-modal__desc"
                >
                  {openProject.description}
                </p>

                <div className="project-modal__meta">
                  <strong>Categories:</strong>
                </div>
                <div className="project-modal__tags">
                  {openProject.categories.map((cat) => (
                    <span key={cat} className="project-modal__tag">
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </span>
                  ))}
                </div>

                <div className="project-modal__actions">
                  <a
                    href="tel:+27824989477"
                    className="project-modal__btn-primary"
                  >
                    <i className="fas fa-phone" /> Call Us About This Project
                  </a>
                  <Link
                    to="/contact"
                    className="project-modal__btn-outline"
                    onClick={handleCloseModal}
                  >
                    <i className="fas fa-envelope" /> Request a Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
